import { FileAction, FileState, useFileAPI } from 'components/common/FileAPI'
import React, { useCallback, useMemo } from 'react'
import { UploadOrderDocumentType, useSubmitGalleryPowerOfAttorney } from 'dataQueries'

import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { FileType } from 'constants/misc'
import { MUIButton } from 'components/common/MUIButton'
import { OrderEndpoint } from 'constants/API'
import { PowerOfAttorneyUpload } from 'components/common/PowerOfAttorneyUpload'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import Stack from '@mui/material/Stack'
import { getSignedURLRetrieverSeq } from 'components/common/FileAPI/fileUtils'
import { useGalleryDeal } from '../../contexts'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

/**
 * @component
 * Renders the component for uploading power of attorney documents in the client gallery.
 * 
 * @example
 * <ClientGalleryUploadPowerAttorney />
 */
export const ClientGalleryUploadPowerAttorney: React.FC = () => {
  const { t } = useTranslation(['power_of_attorney', 'common', 'gallery'])
  const { dealData } = useGalleryDeal()
  const { spawnErrorToast, spawnSuccessToast } = useSnackbar()

  const submitMutation = useSubmitGalleryPowerOfAttorney()

  const onCheckFileSize = (fileSize: number) => {
    // Check if file size is bigger than 5MB
    if (fileSize <= 0 || fileSize > 5 * 1024 * 1024) return false

    return true
  }

  const powerOfAttorneyUpload = useFileAPI('POWER_OF_ATTORNEY', {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => api.post(
      OrderEndpoint.GET_POA_UPLOAD_URL,
      {
        orderId: dealData!.id.toString(),
      },
      {
        type: UploadOrderDocumentType.POWER_OF_ATTORNEY,
        filename: fileEntry.fileObject.name,
        contentType: fileEntry.fileObject.type,
      },
      true
    )),
    deleteHandler: (fileEntry, api) => api.delete(
      OrderEndpoint.DELETE_UPLOADED_POA,
      {
        orderId: dealData!.id.toString(),
        filename: fileEntry.gcFilename || '',
      },
      true,
      {
        params: {
          type: UploadOrderDocumentType.POWER_OF_ATTORNEY
        }
      }
    )
  })

  const powerOfAttorneyRepsUpload = useFileAPI('POWER_OF_ATTORNEY_REPS', {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => api.post(
      OrderEndpoint.GET_POA_UPLOAD_URL,
      {
        orderId: dealData!.id.toString(),
      },
      {
        type: UploadOrderDocumentType.POWER_OF_ATTORNEY,
        filename: fileEntry.fileObject.name,
        contentType: fileEntry.fileObject.type,
      },
      true
    )),
    deleteHandler: (fileEntry, api) => api.delete(
      OrderEndpoint.DELETE_UPLOADED_POA,
      {
        orderId: dealData!.id.toString(),
        filename: fileEntry.gcFilename || '',
      },
      true,
      {
        params: {
          type: UploadOrderDocumentType.POWER_OF_ATTORNEY
        }
      }
    )
  })

  // TO BE UNCOMMENTED WITH https://backbonephoto.atlassian.net/browse/BKBN-2443
  // const powerOfAttorneyOtherFilesUpload = useFileAPI('POWER_OF_ATTORNEY_OTHER_FILES', {
  //   uploadUrlResolver: (fileEntry, api) => {
  //     return api.post(
  //       OrderEndpoint.GET_DOCUMENT_UPLOAD_URL,
  //       { sessionId: dealData!.id.toString() },
  //       {
  //         documentMetadata: {
  //           filename: fileEntry.fileObject.name,
  //           contentType: fileEntry.fileObject.type,
  //           type: DocumentUploadType.INPUT,
  //           tag: UploadTagType.ORDER_SUPPLEMENTARY_DOCUMENT,
  //         }
  //       },

  //       true
  //     )
  //   },
  //   deleteHandler: (_, api) => api.delete(
  //     OrderEndpoint.DELETE_UPLOADED_DOCUMENT,
  //     { sessionId: dealData!.id.toString() },
  //     true
  //   )
  // })

  const isDisabled = useMemo(() => {
    const poaFiles = Object.values(powerOfAttorneyUpload.files)
    const poaRepFiles = Object.values(powerOfAttorneyRepsUpload.files)
    // TO BE UNCOMMENTED WITH https://backbonephoto.atlassian.net/browse/BKBN-2443
    // const poaOtherFiles = Object.values(powerOfAttorneyOtherFilesUpload.files)

    if (poaFiles.length !== 1) return true
    if (poaFiles[0].action !== FileAction.UPLOAD || poaFiles[0].state !== FileState.SUCCESS) return true

    if (!dealData?.powerOfAttorney.requiresPowerOfAttorneyRepresentatives) return false

    if (poaRepFiles.length !== 1) return true
    if (poaRepFiles[0].action !== FileAction.UPLOAD || poaRepFiles[0].state !== FileState.SUCCESS) return true

    // TO BE UNCOMMENTED WITH https://backbonephoto.atlassian.net/browse/BKBN-2443
    // if (poaOtherFiles[0].action !== FileAction.UPLOAD || poaOtherFiles[0].state !== FileState.SUCCESS) return true

    return false
  }, [dealData?.powerOfAttorney.requiresPowerOfAttorneyRepresentatives, powerOfAttorneyRepsUpload.files, powerOfAttorneyUpload.files])

  const handleSubmit = useCallback(() => {
    if (isDisabled) return
    if (!dealData?.id) return

    const poaFilename = Object.values(powerOfAttorneyUpload.files)[0]?.gcFilename
    const poaRepFilename = Object.values(powerOfAttorneyRepsUpload.files)[0]?.gcFilename

    if (!poaFilename) return
    if (!!dealData?.powerOfAttorney.requiresPowerOfAttorneyRepresentatives && !poaRepFilename) return

    submitMutation.mutate(
      {
        orderId: dealData.id,
        powerOfAttorneyFilename: poaFilename,
        powerOfAttorneyRepresentativesFilename: poaRepFilename || undefined,
      },
      {
        onSuccess: () => {
          spawnSuccessToast(t('submit_success'))
        },
        onError: (error) => {
          spawnErrorToast(t('submit_error'), { error: error.request })
        }
      }
    )

  }, [dealData?.id, dealData?.powerOfAttorney.requiresPowerOfAttorneyRepresentatives, isDisabled, powerOfAttorneyRepsUpload.files, powerOfAttorneyUpload.files, spawnErrorToast, spawnSuccessToast, submitMutation, t])

  return (
    <SectionedBorderBox title={t('gallery:client_gallery.missing_documents')} marginTop={2}>
      <Stack gap={2}>

        {dealData?.powerOfAttorney.requiresPowerOfAttorney &&
          <PowerOfAttorneyUpload
            boxTitle={t('upload_box_title_owners')}
            variant="owners"
            badgeText={t('awaiting_signature')}
            boxStyle="bordered"
            uploadSlot={
              <FileAPIUpload
                title={t('power_of_attorney')}
                files={powerOfAttorneyUpload.allFilesArray}
                acceptedFileTypes={[FileType.PDF]}
                isDraggingFile={false}
                fileSizeCheckFailedWarning={t('file_size_warning')}
                onCheckFileSize={onCheckFileSize}
                uploadHandler={(acceptedFiles) => powerOfAttorneyUpload.uploadFiles(acceptedFiles)}
                onDelete={(fileId) => powerOfAttorneyUpload.deleteFiles([fileId])}
              />
            }
          />
        }

        {dealData?.powerOfAttorney.requiresPowerOfAttorneyRepresentatives &&
          <PowerOfAttorneyUpload
            boxTitle={t('power_of_attorney_reps')}
            variant="representatives"
            badgeText={t('awaiting_signature')}
            boxStyle="bordered"
            uploadSlot={
              <FileAPIUpload
                title={t('power_of_attorney_reps')}
                files={powerOfAttorneyRepsUpload.allFilesArray}
                acceptedFileTypes={[FileType.PDF]}
                isDraggingFile={false}
                fileSizeCheckFailedWarning={t('file_size_warning')}
                onCheckFileSize={onCheckFileSize}
                uploadHandler={(acceptedFiles) => powerOfAttorneyRepsUpload.uploadFiles(acceptedFiles)}
                onDelete={(fileId) => powerOfAttorneyRepsUpload.deleteFiles([fileId])}
              />
            }
          />
        }

        {/* Let user upload any other files unlimited */}
        {/* TO BE UNCOMMENTED WITH https://backbonephoto.atlassian.net/browse/BKBN-2443 */}
        {/* <FileAPIUpload
          title={t('other_files_upload')}
          files={powerOfAttorneyOtherFilesUpload.allFilesArray}
          acceptedFileTypes={[FileType.PDF]}
          isDraggingFile={false}
          uploadHandler={(acceptedFiles) => powerOfAttorneyOtherFilesUpload.uploadFiles(acceptedFiles)}
          onDelete={(fileId) => powerOfAttorneyOtherFilesUpload.deleteFiles([fileId])}
          fileSizeCheckFailedWarning={t('purchase_flow:power_of_attorney_step.file_size_warning')}
          onCheckFileSize={onCheckFileSize}
          maxNumberOfFiles={15}
        /> */}

        <MUIButton
          sx={{ alignSelf: 'flex-end' }}
          isLoading={submitMutation.isPending}
          disabled={isDisabled}
          onClick={() => handleSubmit()}
        >
          {t('common:Submit')}
        </MUIButton>

      </Stack>
    </SectionedBorderBox>
  )
}
